import React, { useEffect, useState } from "react";
import moment from "moment-timezone";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import Alert from "../Alert";
import { sendGetRequest, sendPostRequest } from "../../utils";
import NewletterFilters from "./ResidentsFilters";
import { NewsletterStatus } from "../../enums/newsletterStatus";
import ResidentCheckinConfirm from "./ResidentCheckinConfirm";
import ResidentLogs from "./ResidentLogs";
import { Button, IconButton } from "@mui/material";
import {
  checkIfLastVisitIsInLastWeek,
  checkIfThisWeek,
  compareBy,
} from "../../lib/helpers";

function Resident() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filterByStatus, setFilterByStatus] = useState("");
  const [filterBySMSAlerts, setFilterBySMSAlerts] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [search, setSearch] = useState("");
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [openLogs, setOpenLogs] = useState(false);
  const [selectedResident, setSelectedResident] = useState(null);
  const [total, setTotal] = useState(0);

  const statuses = Object.values(NewsletterStatus);
  const statusOptions = statuses.map((status) => ({
    value: status,
    label:
      status.charAt(0).toUpperCase() + status.slice(1).replaceAll("_", " "),
  }));

  const doFetchResidents = () => {
    sendGetRequest("residents", true)
      .then((res) => res.json())
      .then((data) => {
        setData(data.data);
        setFilteredData(data.data);
        setTotal(data.total);
      })
      .catch((err) => setError(JSON.stringify(err)));
  };

  useEffect(() => {
    doFetchResidents();
  }, []);

  useEffect(() => {
    let newFilterData = data;

    if (filterByStatus !== "") {
      newFilterData = newFilterData.filter((x) => x.status === filterByStatus);
    }

    if (filterBySMSAlerts !== "") {
      newFilterData = newFilterData.filter(
        (x) => x.opt_in_sms_alerts === Number(filterBySMSAlerts)
      );
    }

    if (search !== "") {
      newFilterData = newFilterData.filter(
        (x) =>
          x.last_name.toLowerCase().includes(search.toLowerCase()) ||
          x.first_name.toLowerCase().includes(search.toLowerCase()) ||
          x.email.toLowerCase().includes(search.toLowerCase()) ||
          x.phone.toLowerCase().includes(search.toLowerCase())
      );
    }

    //Sort
    if (newFilterData.length) {
      newFilterData = newFilterData
        .slice()
        .sort((a, b) => compareBy(a, b, sortBy));
    }

    setFilteredData(newFilterData);
  }, [data, sortBy, filterByStatus, filterBySMSAlerts, search]);

  const doSetFilterByStatus = (filter) => {
    setFilterByStatus(filter);
  };

  const doSetFilterBySMSAlerts = (filter) => {
    setFilterBySMSAlerts(filter);
  };

  const doSetStatus = (id, status) => {
    sendPostRequest(`residents/update-status/${id}`, { status })
      .then((res) => res.text())
      .then((data) => {
        setSuccess("Statut mis à jour avec succès");
        doFetchResidents();
      })
      .catch((err) => setError(JSON.stringify(err)));
  };

  const generateStatusClass = (status) => {
    if (status === NewsletterStatus.ACTIVE) {
      return "bg-green-100 text-green-700";
    } else if (status === NewsletterStatus.INACTIVE) {
      return "bg-yellow-100 text-yellow-700";
    } else {
      return "bg-red-100 text-red-700";
    }
  };

  const doSetSortBy = (sort) => {
    setSortBy(sort);
  };

  const doSetSearch = (search) => {
    setSearch(search);
  };

  const openCheckInModal = (resident) => {
    setSelectedResident(resident);
    setOpenConfirmation(true);
  };

  const doCheckIn = async () => {
    setOpenConfirmation(false);
    await sendPostRequest(`residents/checkin`, {
      id: selectedResident.id,
    }).then((res) => {
      doFetchResidents();
      setSuccess("Successfully checked in");
    });
    setSelectedResident(null);
  };

  const isAlreadyCheckedIn = (resident) => {
    // check if the resident is already checked in for today
    const today = moment().format("YYYY-MM-DD");
    const lastVisit = moment(resident.last_visit).format("YYYY-MM-DD");
    return lastVisit === today;
  };

  const openLogsModal = (resident) => {
    setSelectedResident(resident);
    setOpenLogs(true);
  };

  const closeLogsModal = () => {
    setOpenLogs(false);
    setSelectedResident(null);
  };

  return (
    <div>
      <Box sx={{ paddingTop: 2, flexGrow: 1 }} className="shadow-lg border">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert error={error} containerClassName="mt-0 mb-2" />
            <Alert success={success} containerClassName="mt-0 mb-2" />
            <TableContainer
              component={Paper}
              className="!border-none !shadow-none"
            >
              <Typography variant="h4" className="p-4 customer-list-header">
                Residents
              </Typography>
              <NewletterFilters
                doSetFilterByStatus={doSetFilterByStatus}
                doSetFilterBySMSAlerts={doSetFilterBySMSAlerts}
                doSetSortBy={doSetSortBy}
                doSetSearch={doSetSearch}
                total={total}
              />
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell className="!font-semibold">First Name</TableCell>
                    <TableCell className="!font-semibold">Last Name</TableCell>
                    <TableCell className="!font-semibold">Email</TableCell>
                    <TableCell className="!font-semibold">Phone #</TableCell>
                    <TableCell className="!font-semibold">
                      <span title="Email Notifications">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="size-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                          />
                        </svg>
                      </span>
                    </TableCell>
                    <TableCell className="!font-semibold">
                      <span title="SMS Alerts">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="size-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z"
                          />
                        </svg>
                      </span>
                    </TableCell>
                    <TableCell className="!font-semibold">Status</TableCell>
                    <TableCell className="!font-semibold">
                      Date Created
                    </TableCell>
                    <TableCell className="!font-semibold">
                      Weekly Streak
                    </TableCell>
                    <TableCell className="!font-semibold">Discount</TableCell>
                    <TableCell className="!font-semibold">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData?.length > 0 ? (
                    <>
                      {filteredData.map((row) => {
                        return (
                          <TableRow
                            key={row.id}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell size="small">{row.first_name}</TableCell>
                            <TableCell size="small">{row.last_name}</TableCell>
                            <TableCell size="small">{row.email}</TableCell>
                            <TableCell size="small">{row.phone}</TableCell>
                            <TableCell size="small">
                              {row.opt_in_email_notifications ? (
                                <span className="text-green-500 font-semibold">
                                  Yes
                                </span>
                              ) : (
                                <span className="text-red-500 font-semibold">
                                  No
                                </span>
                              )}
                            </TableCell>
                            <TableCell size="small">
                              {row.opt_in_sms_alerts ? (
                                <span className="text-green-500 font-semibold">
                                  Yes
                                </span>
                              ) : (
                                <span className="text-red-500 font-semibold">
                                  No
                                </span>
                              )}
                            </TableCell>
                            <TableCell size="small">
                              <select
                                className={`border p-1 rounded-md border-gray-400 ${generateStatusClass(
                                  row.status
                                )}`}
                                onChange={(e) =>
                                  doSetStatus(row.id, e.target.value)
                                }
                                value={row.status}
                              >
                                {statusOptions.map((option) => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </TableCell>
                            <TableCell size="small">
                              {moment(row.created_at).format("YYYY-MM-DD")}
                            </TableCell>
                            <TableCell size="small">
                              {row.last_visit
                                ? checkIfLastVisitIsInLastWeek(row.last_visit)
                                  ? row.current_streak
                                  : checkIfThisWeek(row.last_visit)
                                  ? row.current_streak
                                  : 0
                                : 0}
                            </TableCell>
                            <TableCell size="small">
                              {parseFloat(
                                (row.last_visit
                                  ? row.discount
                                    ? parseFloat(row.discount)
                                    : 15
                                  : 15
                                ).toFixed(2)
                              )}
                              % Off
                            </TableCell>
                            <TableCell size="small">
                              <div className="flex items-center justify-between min-w-[124px]">
                                <Button
                                  type="button"
                                  variant="contained"
                                  size="small"
                                  onClick={() => openCheckInModal(row)}
                                  disabled={isAlreadyCheckedIn(row)}
                                >
                                  Check In
                                </Button>
                                <IconButton
                                  type="button"
                                  size="small"
                                  onClick={() => openLogsModal(row)}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="1.5"
                                    stroke="currentColor"
                                    className="size-6"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
                                    />
                                  </svg>
                                </IconButton>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      <TableRow>
                        <TableCell colSpan={12} size="medium">
                          <div className="text-center">No record found.</div>
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Box>
      <ResidentCheckinConfirm
        openConfirmation={openConfirmation}
        setOpenConfirmation={setOpenConfirmation}
        doCheckIn={doCheckIn}
      />
      <ResidentLogs
        openLogs={openLogs}
        onClose={closeLogsModal}
        selectedResident={selectedResident}
      />
    </div>
  );
}

export default Resident;
